import { useState, useEffect } from "react";
import { Modal, Form, Input, Space, Typography, Select, InputNumber, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { ColorPickerWithLabel } from "../OverviewSettings/ColorPickerWithLabel";

const { Option } = Select;

export const ColorStrategyModal = ({ 
  isVisible, 
  onCancel, 
  onSubmit, 
  editingStrategy = null 
}) => {
  const [selectedColors, setSelectedColors] = useState(['#000000', '#000000']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (editingStrategy) {
      try {
        const logicObj = typeof editingStrategy.logic === 'string' ? JSON.parse(editingStrategy.logic) : editingStrategy.logic;
        const ifArray = logicObj.if;
        
        // The last element is always the else color
        const elseColor = ifArray[ifArray.length - 1];
        
        // Process conditions (excluding the last element which is the else color)
        const conditions = [];
        for (let i = 0; i < ifArray.length - 1; i += 2) {
          const condition = ifArray[i];
          const color = ifArray[i + 1];
          
          // Extract operator (first key in the condition object)
          const operator = Object.keys(condition)[0];
          
          // Extract the comparison details
          const comparison = condition[operator][1];
          const multiplier = comparison?.["*"]?.[1] || 1; // If no multiplier, default to 1
          const varPath = comparison?.["*"]?.[0].var || 'one';
          
          conditions.push({
            operator,
            compareType: varPath === 'one' ? 'constant' : 'baseline',
            constantValue: varPath === 'one' ? 1 : undefined,
            baselineValue: varPath === 'metric_baseline' ? 'metric_baseline' : undefined,
            multiplier,
            color
          });
        }

        // Set up form values
        form.setFieldsValue({
          name: editingStrategy.name,
          conditions,
          elseColor
        });

        // Update color state
        setSelectedColors([...conditions.map(c => c.color), elseColor]);
      } catch (error) {
        console.error("Error parsing strategy logic:", error);
        form.setFieldsValue({
          name: editingStrategy.name
        });
      }
    } else {
      form.resetFields();
      setSelectedColors(['#000000', '#000000']);
    }
  }, [editingStrategy, form]);

  const handleColorChange = (index, _, color) => {
    const newColors = [...selectedColors];
    newColors[index] = color;
    setSelectedColors(newColors);
    if (index === selectedColors.length - 1) {
      // This is the else condition
      form.setFieldValue('elseColor', color);
    } else {
      const conditions = form.getFieldValue('conditions') || [];
      conditions[index] = { ...conditions[index], color };
      form.setFieldValue('conditions', conditions);
    }
  };

  const handleSubmit = (values) => {
    // Construct the JSON Logic for multiple conditions
    const conditions = values.conditions || [];
    const jsonLogic = {
      if: conditions.map(condition => [
        {
          [condition.operator]: [
            {"var": "value"},
            {
              "*": [
                {"var": condition.compareType === 'constant' ? 'one' : 'metric_baseline'},
                condition.multiplier
              ]
            }
          ]
        },
        condition.color,
      ]).flat()
    };

    // Add else color as the last element
    jsonLogic.if.push(form.getFieldValue('elseColor') || "#000000");

    const strategyData = {
      name: values.name,
      logic: jsonLogic
    };

    onSubmit(strategyData, editingStrategy);
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedColors(['#000000', '#000000']);
    onCancel();
  };

  return (
    <Modal
      title={editingStrategy ? "Edit Strategy" : "Add Strategy"}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      style={{ minWidth: '800px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: '100%' }}
        initialValues={{
          conditions: [{}] // Start with one empty condition
        }}
      >
        <Form.Item
          name="name"
          label="Strategy Name"
          rules={[{ required: true, message: "Please enter a strategy name" }]}
        >
          <Input />
        </Form.Item>

        <Form.List name="conditions">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {fields.map((field, index) => (
                <Space key={field.key} size="small" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                  <Typography.Text strong>
                    {index === 0 ? <span style={{marginLeft: '30px'}}>If value</span> : <span>else if value</span>}
                  </Typography.Text>
                  
                  <Form.Item
                    {...field}
                    name={[field.name, 'operator']}
                    rules={[{ required: true, message: "Required" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select defaultValue="=" style={{ width: '100%' }}>
                      <Option value="=">=</Option>
                      <Option value=">">&gt;</Option>
                      <Option value=">=">&gt;=</Option>
                      <Option value="<">&lt;</Option>
                      <Option value="<=">&lt;=</Option>
                    </Select>
                  </Form.Item>

                  <span style={{fontSize: '20px'}}>(</span>

                  <Form.Item
                    {...field}
                    name={[field.name, 'compareType']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  > 
                    <Select style={{ width: '100%' }} placeholder="Select Variable" >
                      <Option value="baseline">Metric Baseline</Option>
                      <Option value="constant">One</Option>
                    </Select>
                  </Form.Item>
                  <Typography.Text>×</Typography.Text>

                  <Form.Item
                    {...field}
                    name={[field.name, 'multiplier']}
                    rules={[{ required: true, message: "Required" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber style={{ width: 100, borderRadius: '5px' }} placeholder="Multiplier" />
                  </Form.Item>
                  <span style={{fontSize: '20px'}}>)</span>

                  <Typography.Text strong>, then</Typography.Text>

                  <Form.Item
                    {...field}
                    name={[field.name, 'color']}
                    style={{ marginBottom: 0 }}
                  >
                    <ColorPickerWithLabel
                      pickerId={`strategyColor${index}`}
                      defaultColor={selectedColors[index] || '#000000'}
                      handleColorChangeParent={(_, color) => handleColorChange(index, _, color)}
                    />
                  </Form.Item>

                  {index > 0 && (
                    <MinusCircleOutlined onClick={() => {
                      remove(field.name);
                      const newColors = [...selectedColors];
                      newColors.splice(index, 1);
                      setSelectedColors(newColors);
                    }} />
                  )}
                </Space>
              ))}

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                    setSelectedColors([...selectedColors.slice(0, -1), '#000000', selectedColors[selectedColors.length - 1]]);
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Condition
                </Button>
              </Form.Item>

              {/* Permanent else condition */}
              <Space size="small" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <Typography.Text strong>else</Typography.Text>
                
                <Form.Item
                  name="elseColor"
                  style={{ marginBottom: 0 }}
                  initialValue="#000000"
                >
                  <ColorPickerWithLabel
                    pickerId="elseColor"
                    defaultColor={selectedColors[selectedColors.length - 1]}
                    handleColorChangeParent={(_, color) => handleColorChange(selectedColors.length - 1, _, color)}
                  />
                </Form.Item>
              </Space>
            </Space>
          )}
        </Form.List>

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Button type="primary" htmlType="submit" disabled={editingStrategy && (!editingStrategy.creator_id && !editingStrategy.team_id)}>
              {editingStrategy ? "Update" : "Create"}
            </Button>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}; 